import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Paper,
  TextField,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Bot,
  X,
  Send,
  Plus,
  MessageSquare,
  Trash2,
  ArrowLeft,
  Search,
  AlertCircle,
  Wrench,
  CheckCircle,
  XCircle,
  SquarePen,
  Edit,
  CheckCircle2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CustomTextArea } from "./CustomTextArea";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "./contexts/AuthContext.jsx";
import copilotService from "../services/copilotService.js";
import { ConfirmModal } from "./ConfirmModal.jsx";
import SuccessPopup from "./SuccessPopup.jsx";
import { Button } from "./Button.jsx";
import { useTheme } from "../theme/useTheme.js";
import {
  SourcesButton,
  SourcesPanel,
  groupSources,
  getToolLabels,
  getRunningToolLabel,
  isExternalUrl,
} from "./CopilotSources.jsx";

// Ancho del chat y del panel de fuentes. Por encima de WIDE_QUERY el panel se
// abre al lado del chat (el drawer se ensancha); por debajo lo tapa.
const CHAT_WIDTH = 560;
const SOURCES_WIDTH = 380;
const WIDE_QUERY = `(min-width:${CHAT_WIDTH + SOURCES_WIDTH + 240}px)`;

const POLL_INTERVAL_MS = 400;
const POLL_DEADLINE_MS = 3 * 60 * 1000;
// Distancia al fondo (px) por debajo de la cual el chat sigue al texto.
const STICK_TO_BOTTOM_PX = 80;

// Ritmo de escritura: lo pendiente se muestra en ~REVEAL_LAG_S, entre MIN y MAX car/s.
const REVEAL_LAG_S = 0.1;
const REVEAL_MIN_CPS = 45;
const REVEAL_MAX_CPS = 2750;

const isInProgress = (message) =>
  message.status === "pending" || message.status === "processing";

/**
 * Muestra el texto recibido poco a poco, acelerando si se queda atrás, como
 * ChatGPT/Claude. Sin `animate` (mensajes del historial) lo muestra entero.
 */
const useSmoothText = (text, animate) => {
  const [length, setLength] = useState(animate ? 0 : text.length);
  const stateRef = useRef({ length, frame: null, last: 0, budget: 0 });
  const textRef = useRef(text);
  textRef.current = text;

  useEffect(() => {
    const state = stateRef.current;
    if (!animate) {
      state.length = text.length;
      setLength(text.length);
      return;
    }
    if (state.frame) return;
    const tick = (now) => {
      const pending = textRef.current.length - state.length;
      if (pending <= 0) {
        Object.assign(state, { frame: null, last: 0, budget: 0 });
        return;
      }
      if (state.last) {
        const cps = Math.min(REVEAL_MAX_CPS, Math.max(REVEAL_MIN_CPS, pending / REVEAL_LAG_S));
        state.budget += (cps * (now - state.last)) / 1000;
      }
      state.last = now;
      if (state.budget >= 1) {
        // Palabras enteras: avanza hasta el final de la palabra en la que cae.
        const fullText = textRef.current;
        let end = Math.min(fullText.length, state.length + Math.floor(state.budget));
        while (end < fullText.length && !/\s/.test(fullText[end])) end++;
        state.budget = Math.max(0, state.budget - (end - state.length));
        state.length = end;
        setLength(end);
      }
      state.frame = requestAnimationFrame(tick);
    };
    state.frame = requestAnimationFrame(tick);
  }, [text, animate]);

  useEffect(() => () => cancelAnimationFrame(stateRef.current.frame), []);

  return text.slice(0, length);
};

const StreamingMarkdown = ({ text, animate, inProgress, onReveal }) => {
  const shown = useSmoothText(text, animate);
  const typing = inProgress || shown.length < text.length;

  useEffect(() => {
    onReveal();
  }, [shown, onReveal]);

  return (
    <div className={typing ? "is-typing" : undefined}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
        }}
      >
        {shown}
      </ReactMarkdown>
    </div>
  );
};

const StreamStatus = ({ label }) => (
  <Typography
    variant="body2"
    sx={{
      lineHeight: 1.4,
      m: 0,
      fontWeight: 500,
      background:
        "linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.4) 100%)",
      backgroundSize: "200% 100%",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      color: "transparent",
      animation: "copilotShimmer 1.6s linear infinite",
      "@keyframes copilotShimmer": {
        from: { backgroundPosition: "100% 0" },
        to: { backgroundPosition: "-100% 0" },
      },
    }}
  >
    {label}…
  </Typography>
);

const CopilotSidebar = ({
  onClose,
  initialPrompt = "",
  initialThreadTitle = null,
  autoSendOnOpen = false,
  preloadedThread = null,
  preloadedMessage = null,
}) => {
  // Auth
  const { user } = useAuth();
  const navigate = useNavigate();
  const { colors, getRoleBranding } = useTheme();
  const branding = getRoleBranding(user?.role);
  const copilotPrimary = branding.primary || colors.primary.main;
  const copilotLight = branding.light || colors.primary.light;
  const copilotDark = branding.dark || colors.primary.dark;

  // Thread management
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(preloadedThread);
  const [loadingThreads, setLoadingThreads] = useState(true);

  // Message management
  const [messages, setMessages] = useState(preloadedMessage ? [preloadedMessage] : []);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);
  const [pollingMessageId, setPollingMessageId] = useState(null);

  // Mensaje cuyas fuentes se ven en el panel lateral (null = panel cerrado).
  const [sourcesMessageId, setSourcesMessageId] = useState(null);
  const isWide = useMediaQuery(WIDE_QUERY);
  const isWideRef = useRef(isWide);
  isWideRef.current = isWide;

  // UI states
  const [view, setView] = useState(preloadedThread || autoSendOnOpen ? "chat" : "threads"); // "threads" or "chat"
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);

  // Title edit states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [displayedTitle, setDisplayedTitle] = useState("");

  // List Item Edit States
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [listEditTitleValue, setListEditTitleValue] = useState("");
  const [isSavingListTitle, setIsSavingListTitle] = useState(false);

  // Popup state
  const [popupState, setPopupState] = useState({
    open: false,
    message: "",
    error: false,
  });

  const showPopup = (message, error = false) => {
    setPopupState({ open: true, message, error });
  };

  const scrollBoxRef = useRef(null);
  const stickToBottomRef = useRef(true);
  // Mensajes cuya respuesta se ha seguido en vivo en esta sesión: solo esos se "escriben".
  const liveMessageIdsRef = useRef(new Set());
  const composerInputRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  // Cada polling lleva su número; uno viejo con una petición en vuelo no se reprograma.
  const pollRunRef = useRef(0);
  const titleAbortRef = useRef(null);
  const typewriterRef = useRef(null);
  const bootstrapPromptSentRef = useRef(false);
  const handleExplicitClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Una fuente es o una ruta interna de la app ("/tickets/12") o una URL de la
  // base de conocimiento. Solo la segunda abre pestaña nueva; el panel no se
  // cierra al navegar porque no bloquea la app de detrás.
  const handleSourceClick = useCallback(
    (url) => {
      if (!url) return;
      if (isExternalUrl(url)) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(url);
    },
    [navigate]
  );
  const handleDrawerClose = useCallback(() => {
    // Prevent implicit close from modal events (Escape/backdrop).
    // Closing is allowed only from explicit UI actions (X buttons).
  }, []);
  const hasContentTitle = useCallback(
    (title) =>
      typeof title === "string" ? title.trim().length > 0 : Boolean(title),
    []
  );

  const scrollToBottomIfStuck = useCallback(() => {
    const box = scrollBoxRef.current;
    if (box && stickToBottomRef.current) box.scrollTop = box.scrollHeight;
  }, []);

  const handleMessagesScroll = useCallback((event) => {
    const box = event.currentTarget;
    stickToBottomRef.current =
      box.scrollHeight - box.scrollTop - box.clientHeight < STICK_TO_BOTTOM_PX;
  }, []);

  // Mensaje nuevo o hilo nuevo: vuelve al fondo aunque el usuario hubiera subido.
  useEffect(() => {
    stickToBottomRef.current = true;
    scrollToBottomIfStuck();
  }, [messages.length, scrollToBottomIfStuck]);

  useEffect(() => {
    scrollToBottomIfStuck();
  }, [messages, scrollToBottomIfStuck]);

  // Load threads on mount
  useEffect(() => {
    loadThreads();
  }, []);

  // Start polling when preloaded data is provided (skip bootstrap flow)
  useEffect(() => {
    if (!preloadedThread || !preloadedMessage) return;
    if (preloadedMessage.status !== "completed" && preloadedMessage.status !== "failed") {
      startPolling(preloadedMessage.id);
    }
    if (!hasContentTitle(preloadedThread.title)) {
      startTitlePolling(preloadedThread.id, preloadedMessage.title_task_id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearTimeout(pollingTimeoutRef.current);
      pollRunRef.current += 1;
      titleAbortRef.current?.abort();
      if (typewriterRef.current) clearInterval(typewriterRef.current);
    };
  }, []);

  // Sync displayedTitle when switching threads (not during animation)
  useEffect(() => {
    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
      typewriterRef.current = null;
    }
    setDisplayedTitle(selectedThread?.title || "");
    setIsEditingTitle(false);
    setEditTitleValue("");
  }, [selectedThread?.id]);

  // Load threads from API
  const loadThreads = async () => {
    try {
      setLoadingThreads(true);
      setError(null);
      const data = await copilotService.listThreads();
      setThreads(
        data.map((thread) =>
          hasContentTitle(thread?.title) ? thread : { ...thread, title: "" }
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingThreads(false);
    }
  };

  // Typewriter animation for title
  const animateTitleTypewriter = useCallback((newTitle) => {
    if (typewriterRef.current) clearInterval(typewriterRef.current);
    let index = 0;
    setDisplayedTitle("");
    typewriterRef.current = setInterval(() => {
      index++;
      setDisplayedTitle(newTitle.slice(0, index));
      if (index >= newTitle.length) {
        clearInterval(typewriterRef.current);
        typewriterRef.current = null;
      }
    }, 40);
  }, []);

  // Poll thread title via service, then animate when found.
  // taskId is the Celery task ID returned by the message creation endpoint;
  // when provided the polling queries Celery state instead of the thread DB record.
  const startTitlePolling = useCallback(
    (threadId, taskId = null) => {
      titleAbortRef.current?.abort();
      const controller = new AbortController();
      titleAbortRef.current = controller;

      copilotService.pollThreadTitle(threadId, {
        taskId,
        maxPolls: 8,
        pollInterval: 1500,
        signal: controller.signal,
        onTitleFound: (title) => {
          if (!hasContentTitle(title)) return;
          setSelectedThread((prev) =>
            prev?.id === threadId ? { ...prev, title } : prev
          );
          setThreads((prev) =>
            prev.map((t) => (t.id === threadId ? { ...t, title } : t))
          );
          animateTitleTypewriter(title);
        },
      });
    },
    [animateTitleTypewriter, hasContentTitle]
  );

  // Title edit handlers
  const handleStartEditTitle = () => {
    setEditTitleValue(selectedThread?.title || "");
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditTitleValue("");
  };

  const handleSaveTitle = async () => {
    const trimmed = editTitleValue.trim();
    if (!trimmed || !selectedThread) return;

    setIsSavingTitle(true);
    try {
      const updated = await copilotService.updateThread(selectedThread.id, {
        title: trimmed,
      });
      const newTitle = updated?.title || trimmed;

      setSelectedThread((prev) => ({ ...prev, title: newTitle }));
      setThreads((prev) =>
        prev.map((t) =>
          t.id === selectedThread.id ? { ...t, title: newTitle } : t
        )
      );
      setDisplayedTitle(newTitle);
      setIsEditingTitle(false);
      setEditTitleValue("");
      showPopup("Título actualizado correctamente");
    } catch (err) {
      setError(err.message);
      showPopup(err.message, true);
    } finally {
      setIsSavingTitle(false);
    }
  };

  // List Edit Handlers
  const handleStartListEdit = (thread, e) => {
    e.stopPropagation();
    setEditingThreadId(thread.id);
    setListEditTitleValue(thread.title || "");
  };

  const handleCancelListEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingThreadId(null);
    setListEditTitleValue("");
  };

  const handleSaveListTitle = async (threadId, e) => {
    if (e) e.stopPropagation();
    const trimmed = listEditTitleValue.trim();
    if (!trimmed) return;

    setIsSavingListTitle(true);
    try {
      const updated = await copilotService.updateThread(threadId, {
        title: trimmed,
      });
      const newTitle = updated?.title || trimmed;

      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, title: newTitle } : t))
      );
      setEditingThreadId(null);
      setListEditTitleValue("");

      // If we edited the currently selected thread, update its title in view too
      if (selectedThread?.id === threadId) {
        setSelectedThread(prev => ({ ...prev, title: newTitle }));
        setDisplayedTitle(newTitle);
      }
      showPopup("Título actualizado correctamente");
    } catch (err) {
      setError(err.message);
      showPopup(err.message, true);
    } finally {
      setIsSavingListTitle(false);
    }
  };

  // Create new thread
  const handleCreateThread = async () => {
    try {
      setError(null);
      const newThread = await copilotService.createThread(null);
      setThreads([newThread, ...threads]);
      setSelectedThread(newThread);
      setMessages([]);
      setSourcesMessageId(null);
      setView("chat");
    } catch (err) {
      setError(err.message);
      showPopup(err.message, true);
    }
  };

  // Select thread and load messages
  const handleSelectThread = async (thread) => {
    if (editingThreadId === thread.id) return; // Prevent selection while editing
    try {
      setError(null);
      setSelectedThread(thread);
      setSourcesMessageId(null);

      // Load thread details with messages
      const threadData = await copilotService.getThread(thread.id);
      setMessages(threadData.messages || []);
      setView("chat");

      // Si se dejó una respuesta a medias, se sigue donde va.
      const unfinished = (threadData.messages || []).findLast(isInProgress);
      if (unfinished) startPolling(unfinished.id);
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete thread
  const handleDeleteThread = async (threadId) => {
    try {
      setError(null);
      await copilotService.deleteThread(threadId);

      // Remove from list
      setThreads(threads.filter((t) => t.id !== threadId));

      // If deleted thread was selected, go back to threads view
      if (selectedThread?.id === threadId) {
        setSelectedThread(null);
        setMessages([]);
        setView("threads");
      }

      setDeleteDialogOpen(false);
      setThreadToDelete(null);
      showPopup("Conversación eliminada correctamente");
    } catch (err) {
      setError(err.message);
      showPopup(err.message, true);
    }
  };

  // Polling of a message until completed/failed, with a deadline. Ends in a visible state.
  const startPolling = useCallback((messageId) => {
    clearTimeout(pollingTimeoutRef.current);
    const run = ++pollRunRef.current;
    liveMessageIdsRef.current.add(messageId);
    setPollingMessageId(messageId);
    const deadline = Date.now() + POLL_DEADLINE_MS;

    const stop = (pollError) => {
      pollingTimeoutRef.current = null;
      setPollingMessageId(null);
      if (pollError) {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, pollError } : msg))
        );
      }
    };

    const poll = async () => {
      let updatedMessage;
      try {
        updatedMessage = await copilotService.pollMessage(messageId);
      } catch (err) {
        if (run !== pollRunRef.current) return;
        console.error("Polling error:", err);
        stop("No se pudo actualizar la respuesta. Vuelve a abrir la conversación.");
        return;
      }
      if (run !== pollRunRef.current) return;

      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? updatedMessage : msg))
      );

      if (!isInProgress(updatedMessage)) {
        stop();
        // Si la respuesta se apoya en documentación, en escritorio se abre
        // el panel de fuentes al lado, sin tapar el chat.
        if (
          isWideRef.current &&
          groupSources(updatedMessage.mcp_metadata.sources).knowledge.length > 0
        ) {
          setSourcesMessageId(messageId);
        }
        return;
      }

      if (Date.now() > deadline) {
        stop("La respuesta está tardando más de lo normal. Vuelve a abrir la conversación en un rato.");
        return;
      }
      pollingTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    pollingTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, []);

  useEffect(() => {
    if (!autoSendOnOpen) return;
    const prompt = typeof initialPrompt === "string" ? initialPrompt.trim() : "";
    if (!prompt) return;
    if (bootstrapPromptSentRef.current) return;
    bootstrapPromptSentRef.current = true;

    // Mensaje optimista: se muestra al instante antes de que la API responda
    const optimisticMsg = {
      id: `optimistic-${Date.now()}`,
      user_input: prompt,
      response: "",
      status: "pending",
      created_at: new Date().toISOString(),
    };
    setMessages([optimisticMsg]);
    setSendingMessage(true);

    let cancelled = false;

    const bootstrapTicketPrompt = async () => {
      try {
        setError(null);

        const createdThread = await copilotService.createThread(
          initialThreadTitle || null
        );

        if (cancelled) return;

        setThreads((prev) => {
          const exists = prev.some((thread) => thread.id === createdThread.id);
          return exists ? prev : [createdThread, ...prev];
        });
        setSelectedThread(createdThread);

        const createdMessage = await copilotService.sendMessage(
          createdThread.id,
          prompt
        );

        if (cancelled) return;

        // Reemplazar el mensaje optimista por el real
        setMessages([createdMessage]);
        startPolling(createdMessage.id);

        if (!hasContentTitle(createdThread.title)) {
          startTitlePolling(createdThread.id, createdMessage.title_task_id ?? null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        showPopup(err.message, true);
        // Quitar el mensaje optimista en caso de error
        setMessages([]);
      } finally {
        if (!cancelled) {
          setSendingMessage(false);
          setNewMessage("");
        }
      }
    };

    bootstrapTicketPrompt();

    return () => {
      cancelled = true;
    };
  }, [
    autoSendOnOpen,
    initialPrompt,
    initialThreadTitle,
    startPolling,
    startTitlePolling,
    hasContentTitle,
  ]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread || sendingMessage) return;

    const messageText = newMessage;
    setNewMessage("");
    setSendingMessage(true);
    setError(null);

    try {
      // Send message to API
      const createdMessage = await copilotService.sendMessage(
        selectedThread.id,
        messageText
      );

      // Add message to list
      setMessages((prev) => [...prev, createdMessage]);

      // Start polling for response
      startPolling(createdMessage.id);

      // Try to get the title from the first message onwards.
      // If the backend has no context yet it returns nothing; the next message will retry.
      // Once a title is found, selectedThread.title is set and this branch won't run again.
      if (!hasContentTitle(selectedThread.title)) {
        startTitlePolling(selectedThread.id, createdMessage.title_task_id ?? null);
      }
    } catch (err) {
      setError(err.message);
      setNewMessage(messageText); // Restore message on error
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Back to threads view
  const handleBackToThreads = () => {
    setView("threads");
    setSelectedThread(null);
    setMessages([]);
    setSourcesMessageId(null);

    clearTimeout(pollingTimeoutRef.current);
    pollingTimeoutRef.current = null;
    pollRunRef.current += 1;
    titleAbortRef.current?.abort();
    setPollingMessageId(null);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={14} />;
      case "failed":
        return <XCircle size={14} />;
      default:
        return null;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#66BB6A";
      case "failed":
        return "#EF5350";
      default:
        return "#999";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Render threads view
  const renderThreadsView = () => (
    <>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          // Cuadra exactamente con el header de la app (64px, borde incluido).
          // minHeight en vez de height para que el campo de editar título
          // pueda seguir creciendo hasta sus 3 líneas.
          minHeight: 64,
          boxSizing: "border-box",
          flexShrink: 0,
          borderBottom: "1px solid #ececf1",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: copilotPrimary,
              width: 28,
              height: 28,
              color: branding.contrastText || colors.primary.contrastText,
            }}
          >
            <Bot size={18} />
          </Avatar>
          <Typography fontWeight={600} color="#3a2f64">
            Markezy Copilot
          </Typography>
          <Chip
            label="Beta"
            size="small"
            sx={{
              bgcolor: alpha(copilotLight, 0.25),
              color: copilotPrimary,
              fontWeight: 500,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
        </Box>

        <IconButton size="small" onClick={handleExplicitClose}>
          <X size={20} />
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ m: 2, mb: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {/* New Thread Button */}
        <Button
          onClick={handleCreateThread}
          sx={{
            mb: 2,
            width: "100%",
          }}
        >
          <Plus size={18} style={{ marginRight: "8px" }} />
          Nueva Conversación
        </Button>

        {/* Loading State */}
        {loadingThreads && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {/* Threads List */}
        {!loadingThreads && threads.length > 0 && (
          <List sx={{ p: 0 }}>
            {threads.map((thread) => {
              const isEditing = editingThreadId === thread.id;

              return (
                <Paper
                  key={thread.id}
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "&:hover": {
                      bgcolor: "#f9f9f9",
                    },
                  }}
                >
                  <ListItem
                    disablePadding
                    secondaryAction={
                      !isEditing && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Editar título">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => handleStartListEdit(thread, e)}
                              sx={{
                                opacity: 0.6,
                                "&:hover": { opacity: 1 },
                              }}
                            >
                              <Edit size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar conversación">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setThreadToDelete(thread);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{
                                opacity: 0.6,
                                "&:hover": { opacity: 1, color: "error.main" },
                              }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    }
                  >
                    {isEditing ? (
                      <Box
                        sx={{
                          py: 1,
                          px: 2,
                          width: "100%",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          cursor: "default",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MessageSquare
                          size={18}
                          color={copilotPrimary}
                          style={{ marginTop: 8 }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                          <CustomTextArea
                            value={listEditTitleValue}
                            onChange={(e) => setListEditTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSaveListTitle(thread.id, e);
                              }
                              if (e.key === "Escape") {
                                e.preventDefault();
                                handleCancelListEdit(e);
                              }
                            }}
                            autoFocus
                            rows={1}
                            maxRows={3}
                            autoExpand
                            sx={{
                              flex: 1,
                              fontSize: "0.9rem",
                              fontWeight: 500,
                            }}
                          />
                          <Tooltip title="Confirmar (Enter)">
                            <span style={{ marginTop: '4px' }}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleSaveListTitle(thread.id, e)}
                                disabled={isSavingListTitle || !listEditTitleValue.trim()}
                              >
                                {isSavingListTitle ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CheckCircle2 size={16} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={handleCancelListEdit}
                            disabled={isSavingListTitle}
                            sx={{ mt: 0.5 }}
                          >
                            <X size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <ListItemButton
                        onClick={() => handleSelectThread(thread)}
                        sx={{
                          cursor: 'pointer',
                          pr: 11, // Extra right padding so ellipsis starts a bit earlier
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                            overflow: "hidden", // Ensure container doesn't leak
                          }}
                        >
                          <MessageSquare
                            size={18}
                            color={copilotPrimary}
                            style={{ marginTop: 0, flexShrink: 0 }} // Don't shrink icon
                          />

                          <ListItemText
                            sx={{ minWidth: 0, flex: 1, mr: 2 }} // Slightly less title width so truncation happens earlier
                            primary={
                              hasContentTitle(thread.title)
                                ? thread.title
                                : "Nueva conversación"
                            }
                            secondary={`${thread.message_count || 0} mensaje${thread.message_count !== 1 ? "s" : ""
                              }`}
                            primaryTypographyProps={{
                              fontWeight: 500,
                              fontSize: "0.9rem",
                              noWrap: true,
                            }}
                            secondaryTypographyProps={{
                              fontSize: "0.75rem",
                              noWrap: true,
                            }}
                          />
                        </Box>
                      </ListItemButton>
                    )}
                  </ListItem>
                </Paper>
              );
            })}
          </List>
        )}

        {/* Empty State */}
        {!loadingThreads && threads.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 2,
              textAlign: "center",
            }}
          >
            <MessageSquare size={48} color="#ccc" />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              No hay conversaciones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crea una nueva conversación para empezar a chatear con el
              asistente IA
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

  // Render chat view
  // Estado del composer: bloqueado mientras se envía o se espera respuesta.
  const composerDisabled = sendingMessage || pollingMessageId !== null;
  const canSendMessage = Boolean(newMessage.trim()) && !composerDisabled;

  const renderChatView = () => (
    <>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          // Cuadra exactamente con el header de la app (64px, borde incluido).
          // minHeight en vez de height para que el campo de editar título
          // pueda seguir creciendo hasta sus 3 líneas.
          minHeight: 64,
          boxSizing: "border-box",
          flexShrink: 0,
          borderBottom: "1px solid #ececf1",
          bgcolor: "#fff",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <IconButton size="small" onClick={handleBackToThreads} sx={{ flexShrink: 0 }}>
            <ArrowLeft size={20} />
          </IconButton>

          {isEditingTitle ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <CustomTextArea
                value={editTitleValue}
                onChange={(e) => setEditTitleValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveTitle();
                  }
                  if (e.key === "Escape") handleCancelEditTitle();
                }}
                disabled={isSavingTitle}
                autoFocus
                rows={1}
                maxRows={3}
                autoExpand
                sx={{
                  flex: 1,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              />
              <Tooltip title="Confirmar (Enter)">
                <span style={{ marginTop: '4px' }}>
                  <IconButton
                    size="small"
                    onClick={handleSaveTitle}
                    disabled={isSavingTitle || !editTitleValue.trim()}
                  >
                    {isSavingTitle ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CheckCircle2 size={16} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancelar (Esc)">
                <IconButton
                  size="small"
                  onClick={handleCancelEditTitle}
                  disabled={isSavingTitle}
                  sx={{ marginTop: '4px' }}
                >
                  <X size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: copilotPrimary,
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  mr: 0.5,
                  color: branding.contrastText || colors.primary.contrastText,
                }}
              >
                <Bot size={18} />
              </Avatar>
              <Tooltip
                title={
                  hasContentTitle(displayedTitle)
                    ? displayedTitle
                    : "Nueva conversación"
                }
                arrow
              >
                <Typography
                  fontWeight={600}
                  color="#3a2f64"
                  noWrap
                  sx={{ flex: 1 }}
                >
                  {hasContentTitle(displayedTitle)
                    ? displayedTitle
                    : "Nueva conversación"}
                </Typography>
              </Tooltip>
              <Tooltip title="Editar título">
                <IconButton
                  size="small"
                  onClick={handleStartEditTitle}
                  sx={{
                    flexShrink: 0,
                    opacity: 0.45,
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <SquarePen size={15} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {!isEditingTitle && (
          <IconButton size="small" onClick={handleExplicitClose} sx={{ flexShrink: 0 }}>
            <X size={20} />
          </IconButton>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ m: 2, mb: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Messages */}
      <Box
        ref={scrollBoxRef}
        onScroll={handleMessagesScroll}
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 3, pb: 2, pt: 2 }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              py: 4,
              px: 2,
              textAlign: "center",
            }}
          >
            <Bot size={48} color={copilotPrimary} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Hola! Soy tu copiloto
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Puedo ayudarte con casi todo: preguntas, procesos, metodología y
              más. ¿Qué necesitas hoy?
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3.5}>
            {messages.map((message) => (
              <Box key={message.id}>
                {/* User Input */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 1.75,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: "82%",
                      px: 1.75,
                      py: 1.25,
                      borderRadius: "18px 18px 6px 18px",
                      backgroundColor: copilotPrimary,
                      color: "white",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontSize: "0.9rem",
                        lineHeight: 1.4,
                      }}
                    >
                      {message.user_input}
                    </Typography>
                  </Paper>
                </Box>

                {/* AI Response */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: message.copilot_response
                      ? "flex-start"
                      : "center",
                    gap: 1.75,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "#f4f4f4",
                      border: "1px solid rgba(0,0,0,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: message.copilot_response ? 0.5 : 0,
                    }}
                  >
                    <Bot size={16} color={copilotPrimary} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    {!message.copilot_response ? (
                      message.status === "failed" ? (
                        <Alert severity="error" sx={{ py: 0.5 }}>
                          {message.mcp_metadata?.error || "Error al generar la respuesta"}
                        </Alert>
                      ) : message.pollError ? (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          {message.pollError}
                        </Alert>
                      ) : isInProgress(message) ? (
                        <Box sx={{ display: "flex", alignItems: "center", minHeight: 28 }}>
                          <StreamStatus
                            label={
                              (message.mcp_metadata && getRunningToolLabel(message.mcp_metadata)) ||
                              "Pensando"
                            }
                          />
                        </Box>
                      ) : (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          El copiloto no ha devuelto respuesta. Vuelve a intentarlo.
                        </Alert>
                      )
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          // Sin burbuja: el texto del asistente respira a ancho
                          // completo, como en ChatGPT/Claude.
                          maxWidth: "100%",
                          px: 0,
                          py: 0.25,
                          borderRadius: 0,
                          backgroundColor: "transparent",
                          border: "none",
                          overflow: "hidden",
                        }}
                      >
                        {/* Response Text */}
                        <Box
                          sx={{
                            fontSize: "0.9rem",
                            lineHeight: 1.5,
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            "& h1": {
                              fontSize: "1.4rem",
                              fontWeight: 700,
                              margin: "0.8em 0 0.5em 0",
                              color: copilotDark,
                            },
                            "& h2": {
                              fontSize: "1.2rem",
                              fontWeight: 700,
                              margin: "0.8em 0 0.5em 0",
                              color: copilotDark,
                            },
                            "& h3": {
                              fontSize: "1.05rem",
                              fontWeight: 600,
                              margin: "0.7em 0 0.4em 0",
                              color: copilotPrimary,
                            },
                            "& p": {
                              margin: "0.5em 0",
                              lineHeight: 1.6,
                            },
                            "& ul, & ol": {
                              margin: "0.5em 0",
                              paddingLeft: "1.5em",
                            },
                            "& li": {
                              margin: "0.3em 0",
                              lineHeight: 1.5,
                            },
                            "& a": {
                              color: copilotPrimary,
                              textDecoration: "none",
                              fontWeight: 500,
                              borderBottom: `1px solid ${alpha(
                                copilotPrimary,
                                0.4
                              )}`,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderBottomColor: copilotPrimary,
                                backgroundColor: alpha(copilotPrimary, 0.12),
                              },
                            },
                            "& code": {
                              backgroundColor: "rgba(0,0,0,0.06)",
                              padding: "0.15em 0.4em",
                              borderRadius: "3px",
                              fontSize: "0.85em",
                              fontFamily: "monospace",
                              wordBreak: "break-all",
                            },
                            "& pre": {
                              backgroundColor: "rgba(0,0,0,0.06)",
                              padding: "0.8em",
                              borderRadius: "6px",
                              overflowX: "auto",
                              margin: "0.8em 0",
                              maxWidth: "100%",
                            },
                            "& pre code": {
                              backgroundColor: "transparent",
                              padding: 0,
                              wordBreak: "normal",
                            },
                            "& blockquote": {
                              borderLeft: `3px solid ${copilotPrimary}`,
                              paddingLeft: "1em",
                              margin: "0.8em 0",
                              color: "rgba(0,0,0,0.7)",
                              fontStyle: "italic",
                            },
                            "& strong": {
                              fontWeight: 600,
                              color: "rgba(0,0,0,0.85)",
                            },
                            "& hr": {
                              border: "none",
                              borderTop: "1px solid rgba(0,0,0,0.1)",
                              margin: "1em 0",
                            },
                            "& table": {
                              maxWidth: "100%",
                              overflowX: "auto",
                              display: "block",
                            },
                            "& .is-typing > :last-child:not(ul, ol)::after, & .is-typing > :is(ul, ol):last-child > li:last-child::after": {
                              content: '""',
                              display: "inline-block",
                              width: "0.5em",
                              height: "1em",
                              ml: "3px",
                              verticalAlign: "text-bottom",
                              borderRadius: "2px",
                              bgcolor: copilotPrimary,
                              animation: "copilotCaret 1s steps(2, start) infinite",
                            },
                            "@keyframes copilotCaret": {
                              to: { visibility: "hidden" },
                            },
                          }}
                        >
                          <StreamingMarkdown
                            text={message.copilot_response}
                            animate={liveMessageIdsRef.current.has(message.id)}
                            inProgress={isInProgress(message)}
                            onReveal={scrollToBottomIfStuck}
                          />
                        </Box>

                        {isInProgress(message) &&
                          !message.pollError &&
                          getRunningToolLabel(message.mcp_metadata) && (
                            <Box sx={{ mt: 1 }}>
                              <StreamStatus label={getRunningToolLabel(message.mcp_metadata)} />
                            </Box>
                          )}

                        {message.pollError && (
                          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                            {message.pollError}
                          </Alert>
                        )}

                        {/* Status & Metadata: al terminar, como los botones de ChatGPT/Claude */}
                        {!isInProgress(message) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mt: 1,
                            pt: 1,
                            borderTop:
                              message.mcp_metadata?.tools_called?.length > 0
                                ? "1px solid rgba(0,0,0,0.08)"
                                : "none",
                          }}
                        >
                          {/* Tools used */}
                          {message.mcp_metadata?.tools_called?.length > 0 && (() => {
                            const toolLabels = getToolLabels(message.mcp_metadata.tools_called);
                            return (
                              <Tooltip title={toolLabels.join(" · ")}>
                                <Chip
                                  icon={<Wrench size={12} />}
                                  label={
                                    toolLabels.length > 1
                                      ? `${toolLabels[0]} +${toolLabels.length - 1}`
                                      : toolLabels[0]
                                  }
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    bgcolor: alpha(copilotLight, 0.18),
                                    color: copilotPrimary,
                                    "& .MuiChip-icon": {
                                      color: copilotPrimary,
                                    },
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}

                          <SourcesButton
                            sources={message.mcp_metadata?.sources}
                            active={sourcesMessageId === message.id}
                            accent={copilotPrimary}
                            onClick={() =>
                              setSourcesMessageId((current) =>
                                current === message.id ? null : message.id
                              )
                            }
                          />

                          {/* Status Indicator */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              ml: "auto",
                            }}
                          >
                            <Box sx={{ color: getStatusColor(message.status) }}>
                              {getStatusIcon(message.status)}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: getStatusColor(message.status),
                                fontWeight: 500,
                              }}
                            >
                              {message.status === "completed" &&
                                formatDate(message.updated_at)}
                              {message.status === "failed" && "Error"}
                            </Typography>
                          </Box>
                        </Box>

                        )}

                        {/* Error Message */}
                        {message.status === "failed" &&
                          message.mcp_metadata?.error && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {message.mcp_metadata.error}
                            </Alert>
                          )}
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Input — tarjeta flotante sobre el fondo claro del panel */}
      <Box sx={{ px: 3, pt: 1, pb: 3.5 }}>
        <Box
          onClick={() => composerInputRef.current?.focus()}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            // Ancho completo, alineado a la izquierda con el avatar del bot
            width: "100%",
            px: 2,
            py: 1.5,
            borderRadius: "16px",
            backgroundColor: "#fff",
            // Flota sobre el panel: sombra en vez de borde/separador
            boxShadow: composerFocused
              ? `0 6px 20px rgba(16,24,40,0.10), 0 0 0 2px ${alpha(copilotPrimary, 0.22)}`
              : "0 2px 10px rgba(16,24,40,0.07)",
            transition: "box-shadow .18s ease",
            cursor: composerDisabled ? "default" : "text",
            opacity: composerDisabled ? 0.75 : 1,
          }}
        >
          <TextField
            fullWidth
            inputRef={composerInputRef}
            placeholder="Escribe lo que necesites"
            variant="standard"
            multiline
            maxRows={6}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setComposerFocused(true)}
            onBlur={() => setComposerFocused(false)}
            disabled={composerDisabled}
            InputProps={{ disableUnderline: true }}
            sx={{
              "& .MuiInputBase-root": {
                px: 0,
                py: 0,
                fontSize: "0.95rem",
                lineHeight: 1.5,
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#9aa0ac",
                opacity: 1,
              },
            }}
          />

          {/* Fila de acciones bajo el texto */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <IconButton
              onClick={handleSendMessage}
              disabled={!canSendMessage}
              aria-label="Enviar mensaje"
              sx={{
                width: 32,
                height: 32,
                p: 0,
                borderRadius: "8px",
                backgroundColor: "transparent",
                color: canSendMessage ? copilotPrimary : "#c2c6cf",
                transition: "color .2s ease, background-color .2s ease",
                "&:hover": {
                  backgroundColor: canSendMessage
                    ? alpha(copilotPrimary, 0.1)
                    : "transparent",
                },
                "&.Mui-disabled": { color: "#c2c6cf" },
              }}
            >
              {sendingMessage ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Send size={18} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </>
  );

  const sourcesMessage =
    view === "chat" && sourcesMessageId !== null
      ? messages.find((m) => m.id === sourcesMessageId)
      : null;
  const sourcesOpenBeside = Boolean(sourcesMessage) && isWide;

  return (
    <>
      <Drawer
        anchor="right"
        open
        onClose={handleDrawerClose}
        variant="temporary"
        ModalProps={{
          hideBackdrop: true,
          disableScrollLock: true,
          disableEscapeKeyDown: true,
          // El Copilot es un panel lateral, no un modal: la app de detrás debe
          // seguir siendo usable (scroll, clics, foco) mientras está abierto.
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
        }}
        sx={{
          zIndex: 1100,
          // El root del Modal cubre todo el viewport aunque no haya backdrop;
          // sin esto intercepta cualquier interacción con la página.
          pointerEvents: "none",
          "& .MuiDrawer-paper": {
            // Solo el panel captura eventos.
            pointerEvents: "auto",
            width: {
              xs: "100%",
              sm: sourcesOpenBeside ? CHAT_WIDTH + SOURCES_WIDTH : CHAT_WIDTH,
            },
            transition: "width .25s ease",
            backgroundColor: "#f7f7f9",
            maxWidth: "100vw",
            boxShadow: "-8px 0 32px rgba(0,0,0,0.10)",
            borderLeft: "1px solid #ececf1",
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            flex: { xs: 1, sm: `0 0 ${CHAT_WIDTH}px` },
            minWidth: 0,
            height: "100%",
          }}
        >
          {view === "threads" ? renderThreadsView() : renderChatView()}
        </Box>

        {sourcesMessage && (
          <Box
            sx={
              isWide
                ? {
                    flex: `0 0 ${SOURCES_WIDTH}px`,
                    height: "100%",
                    borderLeft: "1px solid #ececf1",
                  }
                : { position: "absolute", inset: 0, zIndex: 2 }
            }
          >
            <SourcesPanel
              key={sourcesMessage.id}
              message={sourcesMessage}
              accent={copilotPrimary}
              onOpenSource={handleSourceClick}
              onClose={() => setSourcesMessageId(null)}
            />
          </Box>
        )}
      </Drawer>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setThreadToDelete(null);
        }}
        onConfirm={() => handleDeleteThread(threadToDelete.id)}
        title="Eliminar conversación"
        question="¿Eliminar conversación?"
        description="Esta acción no se puede deshacer. Todos los mensajes de esta conversación se eliminarán permanentemente."
        confirmLabel="Eliminar"
        tone="danger"
      />

      <SuccessPopup
        isOpen={popupState.open}
        message={popupState.message}
        error={popupState.error}
        onClose={() => setPopupState((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default CopilotSidebar;
